import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Instagram, Mail, MapPin, Send } from 'lucide-react';

const CONTACT_EMAIL = 'ale@audioxlmilano.com';
const CONTACT_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL || ''}/api/contact.php`;

const emptyForm = { name: '', email: '', message: '', website: '' };

const inputClassName =
  'w-full bg-transparent border-b border-white/20 focus:border-white py-3 font-body text-lg text-white placeholder-white/50 outline-none transition-colors duration-300';
const labelClassName = 'font-body text-xs tracking-[0.2em] text-white/60 uppercase block mb-3';

const Footer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const [formData, setFormData] = useState(emptyForm);
  // idle | sending | sent | error
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Contact endpoint responded ${response.status}`);
      }

      setStatus('sent');
      setFormData(emptyForm);
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    }
  };

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#050505] border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-24"
        >
          <span className="font-heading font-bold text-sm tracking-[0.3em] text-white/40 uppercase">
            Contact
          </span>
          <h2
            className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl leading-tight mt-6"
            data-testid="contact-heading"
          >
            DROP US
            <br />
            A MESSAGE
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className={labelClassName}>
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  className={inputClassName}
                  placeholder="Your name"
                  data-testid="input-name"
                />
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className={labelClassName}>
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClassName}
                  placeholder="name@example.com"
                  data-testid="input-email"
                />
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className={labelClassName}>
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={5000}
                  rows={5}
                  className={`${inputClassName} resize-none`}
                  placeholder="Tell us about your project..."
                  data-testid="input-message"
                />
              </div>

              {/* Honeypot: hidden from people, filled in only by spam bots */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>

              {/* Status Messages */}
              <div role="status" aria-live="polite">
                {status === 'sent' && (
                  <p className="font-body text-base text-white" data-testid="contact-success">
                    Message sent. You'll get a reply at the email address you entered.
                  </p>
                )}
              </div>
              {status === 'error' && (
                <p role="alert" className="font-body text-sm text-red-400" data-testid="contact-error">
                  The message couldn't be sent. Try again in a few minutes, or write directly to{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="underline underline-offset-4 text-white">
                    {CONTACT_EMAIL}
                  </a>
                  .
                </p>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={status === 'sending'}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`group flex items-center gap-4 px-8 py-4 border border-white/20 hover:border-white hover:bg-white hover:text-black transition-all duration-500 font-heading font-bold text-sm tracking-[0.1em] uppercase ${
                  status === 'sending' ? 'opacity-50 cursor-wait' : ''
                }`}
                data-testid="submit-button"
              >
                {status === 'sending' ? (
                  'Sending…'
                ) : (
                  <>
                    Send Message
                    <Send aria-hidden="true" className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </>
                )}
              </motion.button>

              {/* Brand text under button */}
              <p className="font-body text-white/60 text-sm tracking-widest mt-8 uppercase">
                Audio Specialist and Music Consulting
              </p>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col justify-start"
          >
            {/* Contact Details */}
            <div className="space-y-6">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <Mail aria-hidden="true" className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
                <span className="font-body text-lg text-white/70 group-hover:text-white transition-colors hover-underline">
                  {CONTACT_EMAIL}
                </span>
              </a>

              <a
                href="https://www.instagram.com/audioxlmilano/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-instagram"
              >
                <Instagram aria-hidden="true" className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
                <span className="font-body text-lg text-white/70 group-hover:text-white transition-colors hover-underline">
                  @audioxlmilano
                </span>
              </a>

              <a
                href="https://share.google/cMD5TQmTBqrkd7WDW"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-location"
              >
                <MapPin aria-hidden="true" className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
                <span className="font-body text-lg text-white/70 group-hover:text-white transition-colors hover-underline">
                  Via Francesco De Sanctis 12, Milan, Italy
                </span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-24 pt-8 border-t border-white/10 flex justify-center items-center"
        >
          <p className="font-body text-xs text-white/50 tracking-widest uppercase">
            © {new Date().getFullYear()} AudioXL Milano. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
